import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';

import { IdTypes, createFeatureScope } from 'bundles/product-features';
import EnterpriseProductFeaturesContext from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

const UNITED_STATES_COUNTRY_CODE = 'US';

const { useProductFeatures } = /* @__PURE__ */ createFeatureScope({
  enterprise: {
    // Program-only features (not fetched at org level, but needed for type completeness)
    enableCareerAcademyLaborMarketData: true,
    enableCareerAcademyTestimonialsComponent: true,
    isCareerAcademyLimitedCatalog: false,
    enableRenderDomainsMenu: true,
    enableGenAiHubPages: false,
    programDenyCustomMessage: '',
    // Org-level AND program-level features
    browseOnlyProgram: false,
    programSponsoredByMessage: true,
    enableSkillsDashboard: false,
    enableSkillsInSearchAndBrowse: true,
    programJoinTrackingVerbiage: true,
    allowOrgForCurriculumBuilder: false,
    programRecommendationsAvailableCoursesCopy: false,
    isRolePageEstimatedCompletionTimeDisabled: false,
    personalizedRecommendations: true,
    // Org-level AND program-level features
    programEulaBanner: false,
    programCollectionSectionDescription: true,
    programHideCourseraRecommendations: false,
    enableEnterpriseCustomBadges: false,
    allowShortFormContentBasedOnAudience: true,
    enableInvitedOnlyProgram: false,
    filledProgramCustomMessage: '',
    // Org-level only features
    enableSkillsetRoleFilter: true,
    enableCareerSupport: false,
    allowOrgForSpecializationConfiguration: false,
    enableAcademicDisciplines: false,
    stackabilityOnXdpForEnterpriseLearners: true,
    enableEnterpriseTermsOfUseModal: false,
    enableForceSsoLogin: false,
    enableForceSsoLoginV2Beta: false,
    enableOptimisedEnrollmentSetting: false,
    whitelistOrganizationProgramsForSSOLogin: false,
    enableUnifiedSso: false,
    enableGenericNotificationMessaging: false,
    enableProgramAutoJoin: false,
    orgHomeBannerTagline: '',
    enableOrgScopedHome: false,
    enableAiCoach: true,
  },
});

const EnterpriseOrgProductFeaturesDataProvider = ({ children }: { children: React.ReactNode }) => {
  const thirdPartyOrganization = useThirdPartyOrganization();

  const { features: orgFeatures, loading: isOrgProductFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  // No org resolved from the route (e.g. program routes have no `orgSlug`): leave any page-level product features
  // provider visible instead of shadowing it with `undefined`.
  if (!thirdPartyOrganization?.id) {
    return <>{children}</>;
  }

  return (
    <EnterpriseProductFeaturesContext.Provider
      value={{
        features: {
          /**
           * Program-only features
           * These features are only set at the program level (programFeatures.get() only).
           * Temporarily set to defaults since not applicable at org level.
           */
          isCareerAcademyLimitedCatalogProgram: false,
          enableRenderDomainsMenu: false,
          whitelistOrganizationProgramsForSSOLogin: false,
          isBrowseOnlyProgram: false,
          enableInvitedOnlyProgram: false,
          /**
           * Org-level AND program-level features
           * These features can be set at either org or program level (orgFeatures || programFeatures).
           * At org scope, we only fetch the org-level value.
           */
          programDenyCustomMessage: orgFeatures.get('enterprise', 'programDenyCustomMessage'),
          enableSkillsDashboard: orgFeatures.get('enterprise', 'enableSkillsDashboard'),
          enableSkillsInSearchAndBrowse: orgFeatures.get('enterprise', 'enableSkillsInSearchAndBrowse'),
          // renaming for org-scoped purposes; the underlying product feature key remains 'browseOnlyProgram'.
          isBrowseOnlyOrg: orgFeatures.get('enterprise', 'browseOnlyProgram'),
          // renaming for org-scoped purposes; the underlying product feature key remains 'enableInvitedOnlyProgram'.
          enableInvitedOnlyOrg: orgFeatures.get('enterprise', 'enableInvitedOnlyProgram'),
          shouldShowSponsoredByMessage: orgFeatures.get('enterprise', 'programSponsoredByMessage'),
          shouldUseJoinTrackingVerbiage: orgFeatures.get('enterprise', 'programJoinTrackingVerbiage'),
          allowOrgForCurriculumBuilder: orgFeatures.get('enterprise', 'allowOrgForCurriculumBuilder'),
          programRecommendationsAvailableCoursesCopy: orgFeatures.get(
            'enterprise',
            'programRecommendationsAvailableCoursesCopy'
          ),
          isRolePageEstimatedCompletionTimeDisabled: orgFeatures.get(
            'enterprise',
            'isRolePageEstimatedCompletionTimeDisabled'
          ),
          enablePersonalizedRecommendationsForProgram: orgFeatures.get('enterprise', 'personalizedRecommendations'),
          programEulaBanner: orgFeatures.get('enterprise', 'programEulaBanner'),
          // renaming for org-scoped purposes; the underlying product feature key remains 'programEulaBanner'.
          orgEulaBanner: orgFeatures.get('enterprise', 'programEulaBanner'),
          programCollectionSectionDescription: orgFeatures.get('enterprise', 'programCollectionSectionDescription'),
          programHideCourseraRecommendations: orgFeatures.get('enterprise', 'programHideCourseraRecommendations'),
          enableEnterpriseCustomBadges: orgFeatures.get('enterprise', 'enableEnterpriseCustomBadges'),
          allowShortFormContentBasedOnAudience: orgFeatures.get('enterprise', 'allowShortFormContentBasedOnAudience'),
          filledProgramCustomMessage: orgFeatures.get('enterprise', 'filledProgramCustomMessage'),
          enableGenAiHubPages: orgFeatures.get('enterprise', 'enableGenAiHubPages'),
          enableCareerAcademyLaborMarketData:
            orgFeatures.get('enterprise', 'enableCareerAcademyLaborMarketData') &&
            requestCountry.get() === UNITED_STATES_COUNTRY_CODE,
          isCareerAcademyLimitedCatalogOrg: orgFeatures.get('enterprise', 'isCareerAcademyLimitedCatalog'),
          enableCareerAcademyTestimonialsComponent: orgFeatures.get(
            'enterprise',
            'enableCareerAcademyTestimonialsComponent'
          ),

          /**
           * Org-level only features
           * These features are only set at the org level (orgFeatures.get() only).
           */
          showSkillSetRoleFilter: orgFeatures.get('enterprise', 'enableSkillsetRoleFilter'),
          enableCareerSupport: orgFeatures.get('enterprise', 'enableCareerSupport'),
          allowOrgForSpecializationConfiguration: orgFeatures.get(
            'enterprise',
            'allowOrgForSpecializationConfiguration'
          ),
          enableAcademicDisciplines: orgFeatures.get('enterprise', 'enableAcademicDisciplines'),
          stackabilityOnXdpForEnterpriseLearners: orgFeatures.get(
            'enterprise',
            'stackabilityOnXdpForEnterpriseLearners'
          ),
          enableEnterpriseTermsOfUseModal: orgFeatures.get('enterprise', 'enableEnterpriseTermsOfUseModal'),
          enableForceSsoLogin: orgFeatures.get('enterprise', 'enableForceSsoLogin'),
          enableForceSsoLoginV2Beta: orgFeatures.get('enterprise', 'enableForceSsoLoginV2Beta'),
          enableUnifiedSso: orgFeatures.get('enterprise', 'enableUnifiedSso'),
          enableOptimisedEnrollmentSetting: orgFeatures.get('enterprise', 'enableOptimisedEnrollmentSetting'),
          whitelistOrganizationForSSOLogin: orgFeatures.get('enterprise', 'whitelistOrganizationProgramsForSSOLogin'),
          enableGenericNotificationMessaging: orgFeatures.get('enterprise', 'enableGenericNotificationMessaging'),
          enableProgramAutoJoin: orgFeatures.get('enterprise', 'enableProgramAutoJoin'),
          orgHomeBannerTagline: orgFeatures.get('enterprise', 'orgHomeBannerTagline'),
          enableOrgScopedHome: orgFeatures.get('enterprise', 'enableOrgScopedHome'),
          enableAiCoach: orgFeatures.get('enterprise', 'enableAiCoach'),
        },
        isLoading: isOrgProductFeaturesLoading,
      }}
    >
      {children}
    </EnterpriseProductFeaturesContext.Provider>
  );
};

export default EnterpriseOrgProductFeaturesDataProvider;
